// ════════════════════════════════════════════════════════════════════════════
//  src/services/paymentService.js
//  سرویس یکپارچه پرداخت — تنها نقطه ورود برای همه‌ی خریدهای وب اپ
//  ----------------------------------------------------------------
//  • amount هرگز از سمت کلاینت برای purpose-محور ارسال نمی‌شود
//  • سرور (پورت ۳۰۰۵) با درگاه صحبت می‌کند و مبلغ را resolve می‌کند
//  • Session با JWT (auto-renew)
//  • Idempotency روی request و verify
//  • کیف پول داخلی (real ledger)
//  • auto-detect آدرس سرور (localhost / سرور اصلی / REACT_APP_PAYMENT_API_URL)
// ════════════════════════════════════════════════════════════════════════════

import { useCallback, useState, useEffect } from 'react';

// ────────────────────────────────────────────────────────────────────────────
//  ۱) تشخیص خودکار آدرس سرور پرداخت
// ────────────────────────────────────────────────────────────────────────────
const PAYMENT_PORT = 3005;

function detectPaymentBase() {
  // ۱. اگر در .env تعریف شده
  if (process.env.REACT_APP_PAYMENT_API_URL) {
    return process.env.REACT_APP_PAYMENT_API_URL.replace(/\/+$/, '');
  }
  const host = window.location.hostname;
  // ۲. localhost
  if (host === 'localhost' || host === '127.0.0.1') {
    return `http://localhost:${PAYMENT_PORT}`;
  }
  // ۳. هر host دیگر (مثلاً 85.133.244.19 یا دامنه)
  return '/payment';
}

export const PAYMENT_API_BASE = detectPaymentBase();

// در development لاگ کن، در production نه
if (process.env.NODE_ENV !== 'production') {
  console.log('💳 Payment API Base:', PAYMENT_API_BASE);
}

// ────────────────────────────────────────────────────────────────────────────
//  ۲) ثابت‌ها
// ────────────────────────────────────────────────────────────────────────────
const SESSION_LS_KEY = 'pg_session_v1';
const PENDING_LS_KEY = (uid) => `pending_payment_${uid || 'guest'}`;
const HISTORY_LS_KEY = (uid) => `payment_history_${uid || 'guest'}`;
const SESSION_RENEW_THRESHOLD_MS = 60_000; // ۱ دقیقه قبل از انقضا renew کن
const MAX_HISTORY_ITEMS = 50;

// ────────────────────────────────────────────────────────────────────────────
//  ۳) Purposes — کانالیکال + aliases قدیمی (backward-compatible)
//  نکته: مقادیر canonical همان چیزی است که سرور انتظار دارد.
//  alias ها فقط برای سازگاری با کدهای قدیمی نگه داشته شده‌اند و به همان
//  مقدار canonical اشاره می‌کنند؛ پس همیشه یک چیز به سرور می‌رود.
// ────────────────────────────────────────────────────────────────────────────
export const PAYMENT_PURPOSE = Object.freeze({
  // ─── Canonical (سرور) ───
  COINS:               'coins',
  AI_SUBSCRIPTION:     'ai_subscription',
  WORKBOOK:            'workbook',
  JOZVE:               'jozve',
  LESSON_SUBSCRIPTION: 'lesson_subscription',
  FREE_STUDY:          'free_study',
  STEP_QUIZ_UNLOCK:    'step_quiz_unlock',
  WALLET_CHARGE:       'wallet_charge',
  PRIVATE_CLASS:       'private_class',

  // ─── Backward-compat aliases (اشاره به همان canonical) ───
  SUBSCRIPTION: 'ai_subscription', // کد قدیمی CreateExamAI
  BOOK:         'workbook',
  EXAM:         'step_quiz_unlock',
  WALLET:       'wallet_charge',

  // ─── آینده (سرور باید پشتیبانی کند) ───
  COURSE:       'course',
  CUSTOM:       'custom',
});

export const PURPOSE_LABELS = Object.freeze({
  [PAYMENT_PURPOSE.COINS]:               'خرید سکه',
  [PAYMENT_PURPOSE.AI_SUBSCRIPTION]:     'اشتراک AI',
  [PAYMENT_PURPOSE.WORKBOOK]:            'کتاب کار',
  [PAYMENT_PURPOSE.JOZVE]:               'جزوه',
  [PAYMENT_PURPOSE.LESSON_SUBSCRIPTION]: 'اشتراک درس',
  [PAYMENT_PURPOSE.FREE_STUDY]:          'دوره مطالعه آزاد',
  [PAYMENT_PURPOSE.STEP_QUIZ_UNLOCK]:    'آنلاک آزمون',
  [PAYMENT_PURPOSE.WALLET_CHARGE]:       'شارژ کیف پول',
  [PAYMENT_PURPOSE.PRIVATE_CLASS]:       'کلاس خصوصی',
  [PAYMENT_PURPOSE.COURSE]:              'خرید دوره',
  [PAYMENT_PURPOSE.CUSTOM]:              'پرداخت',
});

// ────────────────────────────────────────────────────────────────────────────
//  ۴) Session (JWT) — auto-managed
// ────────────────────────────────────────────────────────────────────────────
let _sessionCache = null;
let _sessionPromise = null;

function _readSession() {
  try {
    const raw = localStorage.getItem(SESSION_LS_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.token || !s?.userId) return null;
    return s;
  } catch { return null; }
}

function _writeSession(s) {
  _sessionCache = s;
  try { localStorage.setItem(SESSION_LS_KEY, JSON.stringify(s)); } catch {}
}

function _clearSession() {
  _sessionCache = null;
  try { localStorage.removeItem(SESSION_LS_KEY); } catch {}
}

function _isTokenFresh(token) {
  try {
    const p = JSON.parse(atob(token.split('.')[1]));
    if (!p.exp) return true;
    return p.exp * 1000 > Date.now() + SESSION_RENEW_THRESHOLD_MS;
  } catch { return false; }
}

async function _ensureSession(userId) {
  if (!userId) throw new Error('برای پرداخت ابتدا وارد حساب خود شوید');

  const cur = _sessionCache || _readSession();
  if (cur && String(cur.userId) === String(userId) && _isTokenFresh(cur.token)) {
    return cur;
  }

  if (_sessionPromise) return _sessionPromise;
  _sessionPromise = (async () => {
    try {
      const res = await fetch(`${PAYMENT_API_BASE}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: String(userId) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok || !data.token) {
        throw new Error(data.error || 'خطا در ایجاد نشست پرداخت');
      }
      const s = { token: data.token, userId: String(userId) };
      _writeSession(s);
      return s;
    } finally { _sessionPromise = null; }
  })();
  return _sessionPromise;
}

/** بعد از login موفق در AuthContext صدا بزن (اختیاری — خودکار هم می‌شود) */
export async function bindSession(userId) {
  if (!userId) return null;
  try { return await _ensureSession(userId); }
  catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[paymentService.bindSession]', e.message);
    }
    return null;
  }
}

/** در logout صدا بزن */
export function unbindSession() { _clearSession(); }

// ────────────────────────────────────────────────────────────────────────────
//  ۵) fetch پایه با retry منطقی
// ────────────────────────────────────────────────────────────────────────────
function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch {}
  }
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
}

async function _apiCall(path, {
  method = 'GET',
  body,
  userId,
  idempotencyKey,
  _retried = false,
} = {}) {
  const session = userId ? await _ensureSession(userId) : _readSession();

  const headers = { 'Content-Type': 'application/json' };
  if (session?.token) headers.Authorization = `Bearer ${session.token}`;
  if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;

  let res;
  try {
    res = await fetch(`${PAYMENT_API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    const err = new Error('خطا در ارتباط با سرور پرداخت. اتصال اینترنت را بررسی کنید.');
    err.isNetworkError = true;
    throw err;
  }

  let data = {};
  try { data = await res.json(); } catch {}

  // اگر توکن منقضی شده بود → یک بار renew و retry
  if (res.status === 401 && userId && !_retried) {
    _clearSession();
    await _ensureSession(userId);
    return _apiCall(path, { method, body, userId, idempotencyKey, _retried: true });
  }

  if (!res.ok || data.ok === false) {
    const err = new Error(data.error || data.message || `خطای سرور (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// ────────────────────────────────────────────────────────────────────────────
//  ۶) نرمال‌سازی purpose (alias → canonical)
//  مقادیر canonical را برمی‌گرداند.
// ────────────────────────────────────────────────────────────────────────────
function _normalizePurpose(purpose) {
  if (!purpose) return purpose;
  const map = {
    // aliases → canonical
    subscription: 'ai_subscription',
    book: 'workbook',
    exam: 'step_quiz_unlock',
    wallet: 'wallet_charge',
  };
  return map[purpose] || purpose;
}

// ────────────────────────────────────────────────────────────────────────────
//  ۷) requestPayment — سرور مبلغ را resolve می‌کند
//  ⚠️ amount هیچ‌وقت از کلاینت فرستاده نمی‌شود (مگر برای wallet_charge که
//     کاربر خودش مبلغ را وارد می‌کند و آن هم سرور اعتبارسنجی می‌کند).
// ────────────────────────────────────────────────────────────────────────────
export async function requestPayment({
  userId,
  purpose,
  metadata = {},
  description,
  idempotencyKey,
}) {
  if (!userId) throw new Error('userId اجباری است');
  if (!purpose) throw new Error('purpose اجباری است');

  const canonicalPurpose = _normalizePurpose(purpose);
  const iKey = idempotencyKey || newIdempotencyKey();

  const data = await _apiCall('/api/payments/request', {
    method: 'POST',
    body: {
      purpose: canonicalPurpose,
      metadata,
      description: description || '',
    },
    userId,
    idempotencyKey: iKey,
  });

  // نرمال‌سازی خروجی (سرور ممکن است amount یا amountToman بدهد)
  const normalized = {
    paymentId: data.paymentId,
    paymentUrl: data.paymentUrl,
    amount: data.amount ?? data.amountToman ?? 0,
    amountToman: data.amountToman ?? data.amount ?? 0,
    raw: data,
  };

  // ذخیره‌ی pending برای بازگشت از درگاه
  try {
    localStorage.setItem(
      PENDING_LS_KEY(userId),
      JSON.stringify({
        paymentId: normalized.paymentId,
        purpose: canonicalPurpose,
        metadata,
        amount: normalized.amount,
        startedAt: Date.now(),
      })
    );
  } catch {}

  return normalized;
}

// ────────────────────────────────────────────────────────────────────────────
//  ۸) هدایت به درگاه
// ────────────────────────────────────────────────────────────────────────────
export function redirectToGateway(paymentUrl) {
  if (!paymentUrl) throw new Error('paymentUrl نامعتبر است');
  // اگر لینک نسبی بود، دامنه کامل را اضافه کن (مهم برای APK)
  if (paymentUrl.startsWith('/')) {
    window.location.href = `https://yadoo20.ir${paymentUrl}`;
  } else {
    window.location.href = paymentUrl;
  }
}

// ────────────────────────────────────────────────────────────────────────────
//  ۹) startPayment — request + redirect (یک‌کلیکی)
// ────────────────────────────────────────────────────────────────────────────
export async function startPayment({ userId, purpose, metadata, description }) {
  const { paymentId, paymentUrl } = await requestPayment({
    userId, purpose, metadata, description,
  });
  // اطمینان از flush شدن localStorage
  await new Promise((r) => setTimeout(r, 30));
  redirectToGateway(paymentUrl);
  return paymentId;
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۰) parseCallbackParams — خواندن پارامترهای بازگشتی از URL
//  تمام شکل‌های ممکن زرین‌پال و درگاه‌های دیگر را پوشش می‌دهد.
// ────────────────────────────────────────────────────────────────────────────
export function parseCallbackParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    paymentId:
      p.get('paymentId') ||
      p.get('payment_id') ||
      p.get('order_id'),
    authority:
      p.get('Authority') ||
      p.get('authority') ||
      p.get('token') ||
      p.get('track_id'),
    status:
      p.get('Status') ||
      p.get('status') ||
      p.get('result'),
  };
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۱) verifyPayment — تأیید تراکنش پس از بازگشت از درگاه
//  Idempotent: اگر دو بار صدا زده شود، همان نتیجه‌ی اول برمی‌گردد.
// ────────────────────────────────────────────────────────────────────────────
export async function verifyPayment({ paymentId, authority, status }) {
  if (!paymentId || !authority) {
    return { ok: false, success: false, message: 'پارامترهای بازگشتی از درگاه ناقص هستند' };
  }

  let data;
  try {
    data = await _apiCall('/api/payments/verify', {
      method: 'POST',
      body: { paymentId, authority, status },
    });
  } catch (e) {
    return {
      ok: false,
      success: false,
      message: e.message || 'خطا در تأیید پرداخت',
      error: e.message,
    };
  }

  // پاک کردن pending + ثبت در تاریخچه‌ی محلی (best-effort)
  try {
    const userId =
      data.userId ||
      JSON.parse(localStorage.getItem('user') || '{}').id ||
      null;

    if (userId) {
      localStorage.removeItem(PENDING_LS_KEY(userId));

      const key = HISTORY_LS_KEY(userId);
      const history = JSON.parse(localStorage.getItem(key) || '[]');
      history.unshift({
        paymentId,
        authority,
        success: !!data.success,
        refId: data.refId || null,
        amount: data.amount ?? data.amountToman ?? 0,
        purpose: data.purpose || 'unknown',
        metadata: data.metadata || {},
        at: new Date().toISOString(),
      });
      localStorage.setItem(key, JSON.stringify(history.slice(0, MAX_HISTORY_ITEMS)));
    }
  } catch {}

  return data;
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۲) کیف پول داخلی
// ────────────────────────────────────────────────────────────────────────────
export async function getWalletBalance(userId) {
  const d = await _apiCall('/api/wallet/balance', { userId });
  return {
    balance: Number(d.balance) || 0,
    currency: d.currency || 'IRT',
  };
}

export async function getWalletTransactions(userId, limit = 50) {
  const d = await _apiCall(
    `/api/wallet/transactions?limit=${encodeURIComponent(limit)}`,
    { userId }
  );
  return Array.isArray(d.transactions) ? d.transactions : [];
}

/**
 * پرداخت مستقیم از کیف پول داخلی — بدون درگاه.
 * سرور مبلغ را از purpose+metadata resolve می‌کند و از کیف پول کسر می‌کند.
 */
export async function payFromWallet({ userId, purpose, metadata = {}, idempotencyKey }) {
  const canonicalPurpose = _normalizePurpose(purpose);
  const iKey = idempotencyKey || newIdempotencyKey();
  return _apiCall('/api/wallet/pay', {
    method: 'POST',
    body: { purpose: canonicalPurpose, metadata, idempotencyKey: iKey },
    userId,
    idempotencyKey: iKey,
  });
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۳) Helperهای typed — برای همه‌ی بخش‌های اپ
//  در صفحات فقط این‌ها را صدا بزن:
// ────────────────────────────────────────────────────────────────────────────
export const pay = {
  /** CoinsPage.jsx */
  coins: ({ userId, packageId }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.COINS,
      metadata: { packageId },
      description: 'خرید بسته سکه',
    }),

  /** CreateExamAI.jsx — اشتراک AI */
  aiSubscription: ({ userId, plan, months }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.AI_SUBSCRIPTION,
      metadata: { plan, months },
      description: 'اشتراک ساخت آزمون با AI',
    }),

  /** کتاب کار */
  workbook: ({ userId, bookId }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.WORKBOOK,
      metadata: { bookId },
      description: 'خرید کتاب کار',
    }),

  /** جزوه ویژه */
  jozve: ({ userId, grade, field, book, chapterNumber, lessonNumber, title }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.JOZVE,
      metadata: { grade, field, book, chapterNumber, lessonNumber, title },
      description: 'خرید جزوه ویژه',
    }),

  /** اشتراک درس داینامیک — LessonStepsPage.jsx */
  lessonSubscription: ({ userId, projectId }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.LESSON_SUBSCRIPTION,
      metadata: { projectId },
      description: 'اشتراک درس',
    }),

  /** FreeStudyCourse.jsx — کل دوره یا یک ویدیو */
  freeStudy: ({ userId, courseId, videoId, bundle, amountToman }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.FREE_STUDY,
      metadata: {
        courseId,
        videoId: videoId || null,
        bundle: bundle === true,
        amountToman: Number(amountToman),
        kind: bundle ? 'free_study_bundle' : 'free_study_video',
      },
      description: bundle ? 'خرید کل دوره' : 'خرید ویدیو',
    }),

  /** آنلاک آزمون مرحله */
  stepQuizUnlock: ({ userId, quizId, stepId, projectId }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.STEP_QUIZ_UNLOCK,
      metadata: { quizId, stepId, projectId },
      description: 'آنلاک آزمون مرحله',
    }),

  /** شارژ کیف پول — کاربر مبلغ را خودش وارد می‌کند */
  walletCharge: ({ userId, amountToman }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.WALLET_CHARGE,
      metadata: { amountToman: Number(amountToman) },
      description: 'شارژ کیف پول',
    }),

  /** ثبت‌نام کلاس خصوصی — StudentPrivateClassPage.jsx */
  privateClass: ({ userId, courseId, amountToman, fullName }) =>
    startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.PRIVATE_CLASS,
      metadata: { courseId, amountToman, fullName }, // 🆕 ارسال مبلغ و نام برای سرور
      description: 'ثبت‌نام کلاس خصوصی',
    }),
};

// ────────────────────────────────────────────────────────────────────────────
//  ۱۴) applyFulfillment — پس از verify موفق (فقط UI را sync می‌کند)
//  کار اصلی روی سرور انجام شده؛ اینجا فقط refresh می‌کنیم.
// ────────────────────────────────────────────────────────────────────────────
export async function applyFulfillment(result, hooks = {}) {
  if (!result?.success) return { ok: false };

  const {
    refreshUser,
    refreshWallet,
    onCoinsUpdated,
    onSubscriptionActivated,
  } = hooks;

  try {
    if (typeof refreshUser === 'function') {
      try { await refreshUser(); } catch {}
    }
    if (typeof refreshWallet === 'function') {
      try { await refreshWallet(); } catch {}
    }

    if (
      result.purpose === PAYMENT_PURPOSE.COINS &&
      typeof onCoinsUpdated === 'function' &&
      result.userId
    ) {
      try {
        const w = await getWalletBalance(result.userId);
        onCoinsUpdated(w.balance, result.metadata);
      } catch {}
    }

    if (
      result.purpose === PAYMENT_PURPOSE.AI_SUBSCRIPTION &&
      typeof onSubscriptionActivated === 'function'
    ) {
      onSubscriptionActivated(result.metadata);
    }

    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۵) تاریخچه‌ی پرداخت (سرور + fallback به localStorage)
// ────────────────────────────────────────────────────────────────────────────
export async function fetchPaymentHistory(userId) {
  if (!userId) return [];
  try {
    const d = await _apiCall('/api/payments', { userId });
    if (Array.isArray(d.payments)) return d.payments;
  } catch {}
  // fallback به localStorage
  try {
    return JSON.parse(localStorage.getItem(HISTORY_LS_KEY(userId)) || '[]');
  } catch {
    return [];
  }
}

/** تاریخچه‌ی محلی (فقط localStorage) — برای نمایش فوری */
export function getLocalPaymentHistory(userId) {
  if (!userId) return [];
  try {
    return JSON.parse(localStorage.getItem(HISTORY_LS_KEY(userId)) || '[]');
  } catch {
    return [];
  }
}

/** پاک کردن تاریخچه‌ی محلی */
export function clearLocalPaymentHistory(userId) {
  if (!userId) return;
  try { localStorage.removeItem(HISTORY_LS_KEY(userId)); } catch {}
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۶) Utilities
// ────────────────────────────────────────────────────────────────────────────
export function getPendingPayment(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(PENDING_LS_KEY(userId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearPendingPayment(userId) {
  if (!userId) return;
  try { localStorage.removeItem(PENDING_LS_KEY(userId)); } catch {}
}

export function isGatewayConfigured() {
  return Boolean(PAYMENT_API_BASE);
}

export function getGatewayBaseUrl() {
  return PAYMENT_API_BASE;
}

/** چک سلامت سرور پرداخت (برای debugging) */
export async function pingGateway() {
  try {
    const res = await fetch(`${PAYMENT_API_BASE}/health`);
    if (!res.ok) return { ok: false, status: res.status };
    return { ok: true, data: await res.json() };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۷) هوک‌ها
// ────────────────────────────────────────────────────────────────────────────

/**
 * usePayment — یک پرداخت را اجرا می‌کند و loading/error را مدیریت می‌کند.
 * کاربر پس از موفقیت به درگاه می‌رود (از صفحه خارج می‌شود).
 */
export function usePayment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async ({ userId, purpose, metadata, description }) => {
    setLoading(true);
    setError(null);
    try {
      await startPayment({ userId, purpose, metadata, description });
      // بعد از این خط، window.location.href تغییر کرده → کاربر از صفحه می‌رود
    } catch (e) {
      setError(e.message || 'خطای پرداخت');
      setLoading(false);
      throw e;
    }
  }, []);

  return { pay: execute, loading, error };
}

/**
 * useWallet — موجودی کیف پول و تراکنش‌ها
 */
export function useWallet(userId) {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const [b, tx] = await Promise.all([
        getWalletBalance(userId),
        getWalletTransactions(userId, 50),
      ]);
      setBalance(b.balance);
      setTransactions(tx);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { balance, transactions, loading, error, refresh };
}

/**
 * usePaymentHistory — تاریخچه‌ی پرداخت‌ها
 */
export function usePaymentHistory(userId) {
  const [history, setHistory] = useState(() => getLocalPaymentHistory(userId));
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const h = await fetchPaymentHistory(userId);
      setHistory(h);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { history, loading, refresh };
}

// ────────────────────────────────────────────────────────────────────────────
//  ۱۸) Default export (سازگاری با import default)
// ────────────────────────────────────────────────────────────────────────────
export default {
  PAYMENT_PURPOSE,
  PURPOSE_LABELS,
  PAYMENT_API_BASE,

  // Session
  bindSession,
  unbindSession,

  // Core flow
  requestPayment,
  startPayment,
  redirectToGateway,
  parseCallbackParams,
  verifyPayment,

  // Wallet
  getWalletBalance,
  getWalletTransactions,
  payFromWallet,

  // Typed helpers
  pay,

  // Fulfillment
  applyFulfillment,

  // History
  fetchPaymentHistory,
  getLocalPaymentHistory,
  clearLocalPaymentHistory,

  // Utilities
  getPendingPayment,
  clearPendingPayment,
  isGatewayConfigured,
  getGatewayBaseUrl,
  pingGateway,

  // Hooks
  usePayment,
  useWallet,
  usePaymentHistory,
};